import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useAnalytics } from '@features/Analytics';
import {
  selectedPlanAtom,
  stripePaymentDeclinedOfferAtom,
  stripePaymentErrorAtom,
  userAtom,
  userPaymentsInfoAtom,
} from '@features/Stripe/atoms';
import { useStripeTryAnotherCard } from '@features/Stripe/hooks';
import { schedulePaymentRecovery } from '@features/Stripe/utils/schedule-payment-recovery';
import {
  isStripePaymentDeclinedDismissTryAnotherCardError,
  isStripePaymentDeclinedModalError,
} from '@features/Stripe/utils/stripe-payment-errors';
import { Image } from 'src/components/common/Image';
import CloseButton from 'src/components/common/close-button/close-button';
import Alert from 'src/components/common/modal/alert/alert';
import { UiKitLoadingButton } from 'src/components/common/ui-kit-loading-button';

import styles from './payment-declined-modal.module.css';

export const PaymentDeclinedModal = ({ onClose }: { onClose: () => void }) => {
  const [
    {
      wasPaymentDeclinedOfferAccepted = false,
      declineCode,
      errorCode,
      paymentMethod,
    },
    setPaymentError,
  ] = useAtom(stripePaymentErrorAtom);
  const [
    { paymentDeclinedShownCount, paymentDeclinedTappedCount },
    setPaymentDeclinedOffer,
  ] = useAtom(stripePaymentDeclinedOfferAtom);
  const { priceId: selectedPlanId } = useAtomValue(selectedPlanAtom);
  const { subscriptionId } = useAtomValue(userPaymentsInfoAtom);
  const { userId } = useAtomValue(userAtom);
  const { trackGoogleEvent } = useAnalytics();
  const { showTryAnotherCardModal } = useStripeTryAnotherCard();
  const wasModalShownTracked = useRef(false);
  const wasDiscountErrorHandled = useRef(false);

  const getAnalyticsOptions = useCallback(
    ({
      shownCount,
      tappedCount,
    }: {
      shownCount: number;
      tappedCount: number;
    }) => ({
      payment_declined_shown_count: shownCount,
      payment_declined_taped_count: tappedCount,
      payment_method: paymentMethod || 'undefined',
      plan_id: selectedPlanId || 'undefined',
      subscription_id: subscriptionId,
      user_id: userId,
    }),
    [paymentMethod, selectedPlanId, subscriptionId, userId],
  );

  const showErrorModal = useMemo(
    () =>
      !wasPaymentDeclinedOfferAccepted &&
      isStripePaymentDeclinedModalError({
        error: {
          code: errorCode,
          decline_code: declineCode,
        },
      }),
    [declineCode, errorCode, wasPaymentDeclinedOfferAccepted],
  );

  const shouldShowTryAnotherCardOnDismiss = useMemo(
    () =>
      isStripePaymentDeclinedDismissTryAnotherCardError({
        error: {
          code: errorCode,
          decline_code: declineCode,
        },
      }),
    [declineCode, errorCode],
  );

  const shouldShowTryAnotherCardAfterDiscountError = useMemo(
    () =>
      wasPaymentDeclinedOfferAccepted &&
      isStripePaymentDeclinedModalError({
        error: {
          code: errorCode,
          decline_code: declineCode,
        },
      }),
    [declineCode, errorCode, wasPaymentDeclinedOfferAccepted],
  );

  const trackPaymentDeclinedShown = useCallback(() => {
    let analyticsCounters = {
      shownCount: paymentDeclinedShownCount + 1,
      tappedCount: paymentDeclinedTappedCount,
    };

    setPaymentDeclinedOffer((previous) => {
      analyticsCounters = {
        shownCount: previous.paymentDeclinedShownCount + 1,
        tappedCount: previous.paymentDeclinedTappedCount,
      };

      return {
        ...previous,
        paymentDeclinedShownCount: analyticsCounters.shownCount,
      };
    });

    trackGoogleEvent({
      eventName: 'payment_declined_shown',
      options: getAnalyticsOptions(analyticsCounters),
    });
  }, [
    getAnalyticsOptions,
    paymentDeclinedShownCount,
    paymentDeclinedTappedCount,
    setPaymentDeclinedOffer,
    trackGoogleEvent,
  ]);

  const trackPaymentDeclinedTapped = useCallback(() => {
    let analyticsCounters = {
      shownCount: paymentDeclinedShownCount,
      tappedCount: paymentDeclinedTappedCount + 1,
    };

    setPaymentDeclinedOffer((previous) => {
      analyticsCounters = {
        shownCount: previous.paymentDeclinedShownCount,
        tappedCount: previous.paymentDeclinedTappedCount + 1,
      };

      return {
        ...previous,
        paymentDeclinedTappedCount: analyticsCounters.tappedCount,
      };
    });

    trackGoogleEvent({
      eventName: 'payment_declined_taped',
      options: getAnalyticsOptions(analyticsCounters),
    });
  }, [
    getAnalyticsOptions,
    paymentDeclinedShownCount,
    paymentDeclinedTappedCount,
    setPaymentDeclinedOffer,
    trackGoogleEvent,
  ]);

  const clearPaymentError = useCallback(
    ({
      wasOfferAccepted,
    }: {
      wasOfferAccepted: boolean;
    }) => {
      setPaymentError((previous) => ({
        ...previous,
        wasPaymentDeclinedOfferAccepted: wasOfferAccepted,
        errorCode: '',
        declineCode: '',
        paymentMethod: '',
        paymentSystem: '',
      }));
    },
    [setPaymentError],
  );

  const handleModelDismiss = useCallback(() => {
    clearPaymentError({ wasOfferAccepted: false });
    trackGoogleEvent({ eventName: 'payment_declined_dismissed' });

    if (shouldShowTryAnotherCardOnDismiss) {
      schedulePaymentRecovery(() =>
        showTryAnotherCardModal({
          paymentMethod: paymentMethod || 'undefined',
          planId: selectedPlanId || 'undefined',
        }),
      );
    }
  }, [
    clearPaymentError,
    paymentMethod,
    selectedPlanId,
    trackGoogleEvent,
    shouldShowTryAnotherCardOnDismiss,
    showTryAnotherCardModal,
  ]);

  const showTryAnotherCardAfterDeclinedOffer = useCallback(() => {
    clearPaymentError({ wasOfferAccepted: true });

    schedulePaymentRecovery(() =>
      showTryAnotherCardModal({
        paymentMethod: paymentMethod || 'undefined',
        planId: selectedPlanId || 'undefined',
      }),
    );
  }, [
    clearPaymentError,
    paymentMethod,
    selectedPlanId,
    showTryAnotherCardModal,
  ]);

  const handleClaimOffer = useCallback(() => {
    trackPaymentDeclinedTapped();
    onClose();
    clearPaymentError({ wasOfferAccepted: true });
  }, [clearPaymentError, onClose, trackPaymentDeclinedTapped]);

  useEffect(() => {
    if (showErrorModal && !wasModalShownTracked.current) {
      trackPaymentDeclinedShown();
      wasModalShownTracked.current = true;
    }

    if (!showErrorModal) {
      wasModalShownTracked.current = false;
    }
  }, [showErrorModal, trackPaymentDeclinedShown]);

  useEffect(() => {
    if (
      shouldShowTryAnotherCardAfterDiscountError &&
      !wasDiscountErrorHandled.current
    ) {
      wasDiscountErrorHandled.current = true;
      showTryAnotherCardAfterDeclinedOffer();
    }

    if (!shouldShowTryAnotherCardAfterDiscountError) {
      wasDiscountErrorHandled.current = false;
    }
  }, [
    shouldShowTryAnotherCardAfterDiscountError,
    showTryAnotherCardAfterDeclinedOffer,
  ]);

  return (
    <Alert
      canClose={false}
      show={showErrorModal}
      onHide={() => undefined}
      modalContentClassName={styles.modalContent}
    >
      <CloseButton
        className={styles.closeButton}
        onClick={handleModelDismiss}
      />
      <div className={styles.imageWrapper}>
        <Image
          src="oops-ice-cream.webp"
          width={142}
          height={109}
          alt="oops-ice-cream"
        />
      </div>
      <h2 className={styles.title}>Let&apos;s make this a little easier</h2>
      <p className={styles.subTitle}>
        The payment didn&apos;t go through, but we&apos;re here to ensure your
        reflection stays fabulous!
      </p>
      <div className={styles.buttonContentContainer}>
        <p className={styles.buttonContentTitle}>
          So, here&apos;s a little treat!
        </p>
        <p className={styles.buttonContentSubTitle}>
          Enjoy one full month just for{' '}
          <span className={styles.priceLabel}>99% off</span>
        </p>
        <UiKitLoadingButton
          data-testid="decline-agree-button"
          onClick={handleClaimOffer}
          className={styles.button}
          type="button"
        >
          Claim Offer
        </UiKitLoadingButton>
      </div>
    </Alert>
  );
};
